import { FC, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import type { Identifier, XYCoord } from "dnd-core";
import { DraggableIcon } from "../../../../../ui/icons";
import { PureVoidFunc } from "../../../../../helpers/functions/types";
import { SMALL_ICON_SIZE_STYLES } from "../../../../../ui/styles";

export interface SortableBadgeProps {
  id: string;
  text?: string;
  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  dropCard: PureVoidFunc;
  keyboardMove: (targetId: string, arrowKey: string) => void;
  typeId: string;
  disabled: boolean;
  activeItemIndex?: number;
}

interface SortableItem {
  index: number;
  id: string;
  type: string;
  disabled: boolean;
}

export const SortableBadge: FC<SortableBadgeProps> = ({
  id,
  text,
  index,
  moveCard,
  dropCard,
  keyboardMove,
  typeId,
  disabled,
  activeItemIndex,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [{ handlerId }, drop] = useDrop<
    SortableItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: typeId,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: SortableItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();

      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [, drag] = useDrag({
    type: typeId,
    item: () => {
      return { id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    end() {
      dropCard();
    },
  });

  drag(drop(ref));

  return (
    <div
      id={id}
      ref={ref}
      className={`cursor-move disabled:cursor-not-allowed ${index === activeItemIndex ? "invert transition " : ""} flex rounded-md px-3 py-2 duration-500 bg-stone-700 text-white dark:bg-stone-300 dark:text-stone-900 text-sm`}
      data-handler-id={handlerId}
      tabIndex={0}
      onKeyUpCapture={({ key }) => {
        if (key === "ArrowLeft" || key === "ArrowRight") {
          keyboardMove(id, key);
        }
      }}
    >
      {!disabled && (
        <DraggableIcon
          className={`${SMALL_ICON_SIZE_STYLES} my-auto text-white dark:text-stone-900`}
        />
      )}
      {text}
    </div>
  );
};
